import { IViewLayer } from "./abstraction/IViewLayer";
import { IViewHost } from "./abstraction/IViewHost";
import { IBounds, IPoint, IRect, ISize, boundsRect, pointDistance, pointEquals} from "../helpers/Geometry";
import { CanvasViewLayer } from "./CanvasViewLayer";
import { SpectrumDraw } from "./draws/SpectrumDraw";
import { NotesDraw } from "./draws/NotesDraw";
import { PitchTrack } from "./tracks/PitchTrack";
import { SongTrack } from "./tracks/SongTrack";
import { TimeDraw } from "./draws/TimeDraw";
import { getNotes } from "../helpers/Notes";
import { IViewTool } from "./abstraction/IViewTool";
import { PanTool } from "./tools/PanTool";
import { PositionDraw } from "./draws/PositionDraw";
import { PitchDetector } from "../analyzer/PitchDetector";
import { SeekTool } from "./tools/SeekTool";
import { Song } from "../player/Song";
import { easeInOut } from "../helpers/Math";
import { IMappedEventTarget } from "../Types";

interface IAnimation {

    status: "off" | "on" | "cancel";

    stopPromise: Promise<void>;

    category: string;
}

type TransformChangeReason = "view" | "scale" | "position";

type SongViewEventMap = {
    "positionChanged": CustomEvent,
    "viewChanged": CustomEvent<TransformChangeReason>,
}

export class SongView extends EventTarget implements IViewHost, IMappedEventTarget<SongViewEventMap> {

    protected _viewTransform: DOMMatrix;
    protected _viewTransformInverse: DOMMatrix;
    protected _viewRect: IRect;
    protected _container: HTMLElement;
    protected _viewBounds: IBounds;
    protected _origin: IPoint;
    protected _pixelPerSeconds: number;
    protected _pixelPerHertz: number;
    protected _layers: IViewLayer[];
    protected _suspendRender: number;
    protected _songTrack: SongTrack;
    protected _pitchTrack: PitchTrack;
    protected _specDraw: SpectrumDraw;
    protected _timeDraw: TimeDraw;
    protected _tools: IViewTool[];
    protected _position: number;
    protected _animations: IAnimation[];
    protected _targetOrigin: IPoint;
    protected _isRenderLoop: boolean;
    protected _renderPromises: { (): void }[];
    protected _suspendMaxCheck = 0;

    constructor() {
        super();

        this._layers = [];
        this._tools = [];
        this._suspendRender = 0;
        this._pixelPerSeconds = 100;
        this._pixelPerHertz = 300;
        this._position = 0;
        this._animations = [];
        this._renderPromises = [];
        this.margin = { x: 0, y: 0 };

        this._origin = {
            x: 0,
            y: 10.2
        }

        const main = this.addLayer(new CanvasViewLayer());


        this._songTrack = main.addDraw(new SongTrack());

        this._timeDraw = main.addDraw(new TimeDraw());
        main.addDraw(new NotesDraw());
        this._pitchTrack = main.addDraw(new PitchTrack());
        main.addDraw(new PositionDraw());

        this._specDraw = main.addDraw(new SpectrumDraw());

        this.addTool(new PanTool(), true);
        this.addTool(new SeekTool(), true);
    }

    async setSong(song: Song) {

        this._songTrack.setSong(song);

        if (song) {
            this._timeDraw.bpm = song.bpm;
            this._timeDraw.tempo = song.tempo;
        }


        this.requestRenderAsync();
    }

    attach(container: HTMLElement) {

        this._container = container;

        for (const layer of this._layers)
            layer.attach(this);

        for (const tool of this._tools)
            tool.attach(this);

        const resizeObserver = new ResizeObserver(entries => {
            this.updateTransform("view");
        });

        resizeObserver.observe(this._container);

        this.updateTransform("view");

        this.renderLoop();
    }

    protected adjustViewMax() {

        if (!this.viewRect || this._suspendMaxCheck)
            return;

        const maxViewRect = this.getMaxViewRect();

        const viewSize = this.getViewSize();

        const maxScaleY = viewSize.height / maxViewRect.height;

        if (this._pixelPerHertz > maxScaleY) {
            //this._pixelPerHertz = maxScaleY;
            //this.computeTransform();
        }

        if (this.viewRect.x < maxViewRect.x)
            this._origin.x += maxViewRect.x - this.viewRect.x;

        /*
        if (this.viewRect.x + this.viewRect.width > maxViewRect.x + maxViewRect.width)
            this._origin.x -= (this.viewRect.x + this.viewRect.width) - (maxViewRect.x + maxViewRect.width);
        */

        if (this.viewRect.y < maxViewRect.y)
            this._origin.y += maxViewRect.y - this.viewRect.y;

        /*
        if (this.viewRect.y + this.viewRect.height > maxViewRect.y + maxViewRect.height)
            this._origin.y -= (this.viewRect.y + this.viewRect.height) - (maxViewRect.y + maxViewRect.height);
        */
        this.computeTransform();
    }

    updateTransform(reason: TransformChangeReason) {

        const viewSize = this.getViewSize();

        this.computeTransform();

        this.adjustViewMax();

        this._viewBounds = {
            tl: this.viewToWorld({ x: 0, y: 0 }),
            tr: this.viewToWorld({ x: viewSize.width, y: 0 }),
            bl: this.viewToWorld({ x: 0, y: viewSize.height }),
            br: this.viewToWorld({ x: viewSize.width, y: viewSize.height })
        }

        this._viewRect = boundsRect(this._viewBounds);

        this.onViewChanged(reason);

        this.requestRenderAsync();
    }

    protected computeTransform() {

        //const viewSize = this.getViewSize();

        this._viewTransform = new DOMMatrix();

        this._viewTransform.translateSelf(65, 20);

        this._viewTransform.scaleSelf(this._pixelPerSeconds, this._pixelPerHertz);

        this._viewTransform.translateSelf(-this._origin.x, -this._origin.y);

        this._viewTransform.m44 = 1;

        this._viewTransformInverse = this._viewTransform.inverse();
    }

    async requestRenderAsync(layer?: IViewLayer) {

        if (this._isRenderLoop) {

            await new Promise<void>(res => this._renderPromises.push(res));
            return true;
        }

        return new Promise<boolean>((res) => {

            if (this._suspendRender > 0) {
                res(false);
                return;
            }

            window.requestAnimationFrame(() => {

                if (layer)
                    layer.render();

                else {
                    for (const curLayer of this._layers) {
                        if (!curLayer.isRendering)
                            curLayer.render();
                    }
                }

                res(true);
            });
        });
    }

    renderLoop() {

        this._isRenderLoop = true;

        let lastRenderTime: number = 0;

        const render = () => {

            if (performance.now() - lastRenderTime > 10) {

                for (const curLayer of this._layers) {
                    if (!curLayer.isRendering)
                        curLayer.render();
                }

                for (const res of this._renderPromises)
                    res();

                this._renderPromises = [];

                lastRenderTime = performance.now();
            }
            
            window.requestAnimationFrame(render);
        }

        window.requestAnimationFrame(render);
    }

    async stopAnimationsAsync(category?: string) {

        const stopAnimations = this._animations.filter(a => (!category || a.category == category) && a.status == "on");

        stopAnimations.forEach(a => a.status == "cancel");

        await Promise.all(stopAnimations.map(a => a.stopPromise));
    }

    async animateAsync<T>(init: () => T, step: (s: T, t: number) => void, durationMs: number, easeFunc: (t: number)=> number, category: string, layer?: IViewLayer) {

        const startTime = performance.now();
        const startValue = init();;

        await this.stopAnimationsAsync(category);

        const newAnimation = {
            category,
            status: "off"
        } as IAnimation;

        newAnimation.stopPromise = new Promise(async res => {

            newAnimation.status = "on";

            this._suspendMaxCheck++;

            while (newAnimation.status == "on") {

                let t = (performance.now() - startTime) / durationMs;

                if (t < 0)
                    t = 0;

                if (t > 1)
                    t = 1;

                t = easeFunc(t);

                this._suspendRender++;

                step(startValue, t);

                this._suspendRender--;

                await this.requestRenderAsync(layer);

                if (t == 1)
                    break;
            }

            newAnimation.status = "off";

            await this.requestRenderAsync(layer);

            res();
        });

        await newAnimation.stopPromise;

        this._animations.splice(this._animations.indexOf(newAnimation), 1);

        this._suspendMaxCheck--;
    }


    async animateOrigin(origin: IPoint, speed: number) {

        if (this._targetOrigin && pointEquals(this._targetOrigin, origin, 0.001))
            return;

        const curPos = this.worldToView(this.origin);
        const newPos = this.worldToView(origin);
        const distancePx = pointDistance(curPos, newPos);
        const duration = Math.min(1000, distancePx / (speed / 1000));

        this._targetOrigin = origin;

        await this.animateAsync(() => ({ ...this._origin }), (s, t) => {

            this.origin = {
                x: s.x + (origin.x - s.x) * t,
                y: s.y + (origin.y - s.y) * t,
            }

        }, duration, easeInOut(), "view");


    } 

    getMaxViewRect() {

        const notes = getNotes();

        return {
            x: 0,
            y: Math.log2(notes[0].freq),
            width: this._songTrack.song ? this._songTrack.duration : 5 * 60,
            height: Math.log2(notes[notes.length - 1].freq) - Math.log2(notes[0].freq)
        } as IRect;

    }

    getViewSize(): ISize {
        return {
            width: Math.max(1, this._container.clientWidth * window.devicePixelRatio),
            height: Math.max(1, this._container.clientHeight * window.devicePixelRatio)
        };
    }

    worldToView(point: IPoint): IPoint {
        return this._viewTransform.transformPoint(point);
    }

    viewToWorld(point: IPoint): IPoint {
        return this._viewTransformInverse.transformPoint(point);
    }

    addTool<T extends IViewTool>(tool: T, isActive: boolean) {

        this._tools.push(tool);

        tool.configure({ isActive });

        if (this._container)
            tool.attach(this);

        return tool;
    }

    addLayer<T extends IViewLayer>(layer: T): T {

        this._layers.push(layer);
        return layer;
    }

    addPitch(time: number, pitch: number, power: number) {

        this._pitchTrack.add(time, pitch ? { pitch, power } : undefined);
    }

    setDetector(value: PitchDetector) {
        this._specDraw.detector = value;
    }

    beginUpdate() {
        this._suspendRender++;
    }

    endUpdate() {
        this._suspendRender--;
        if (this._suspendRender == 0)
            this.requestRenderAsync();
    }

    onViewChanged(reason: TransformChangeReason) {
        this.dispatchEvent(new CustomEvent("viewChanged", { detail: reason }));
    }

    onPositionChanged() {
        this.dispatchEvent(new CustomEvent("positionChanged"));
    }

    get container() {
        return this._container;
    }

    get viewTransform() {
        return this._viewTransform;
    }

    get viewRect() {
        return this._viewRect;
    }

    get pixelPerSeconds() {
        return this._pixelPerSeconds;
    }

    set pixelPerSeconds(value: number) {
        if (this._pixelPerSeconds == value)
            return;
        this._pixelPerSeconds = value;
        this.updateTransform("scale");
    }

    get pixelPerHertz() {
        return this._pixelPerHertz;
    }
    set pixelPerHertz(value: number) {
        if (this._pixelPerHertz == value)
            return;
        this._pixelPerHertz = value;
        this.updateTransform("scale");
    }

    get origin() {
        return this._origin;
    }

    set origin(value: IPoint) {
        if (this._origin == value)
            return;
        this._origin = value;
        this.updateTransform("position");
    }

    get songTrack() {
        return this._songTrack;
    }

    get pitchTrack() {
        return this._pitchTrack;
    }

    get position() {
        return this._position;
    }

    set position(time: number) {
        if (this._position == time)
            return;
        this._position = time;
        this.onPositionChanged();
        this.requestRenderAsync();
    }

    readonly margin: IPoint;
}
